import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Upload,
  CheckCircle2,
  Trash2,
  Wifi,
  Package,
  Activity,
  AlertTriangle,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";

export const OTA = {
  uploadButton: "ota-upload-button",
  uploadVersion: "ota-upload-version",
  uploadNotes: "ota-upload-notes",
  uploadFile: "ota-upload-file",
  uploadSubmit: "ota-upload-submit",
};

function formatBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function Firmware() {
  const { current, currentId } = useProject();
  const [firmwares, setFirmwares] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ version: "", notes: "" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetch_ = async () => {
    if (!currentId) return;
    const { data } = await api.get(`/projects/${currentId}/firmwares`);
    setFirmwares(data);
  };

  useEffect(() => {
    fetch_();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Pilih file .bin");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("version", form.version);
      fd.append("notes", form.notes);
      await api.post(`/projects/${currentId}/firmwares/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Firmware diunggah");
      setOpen(false);
      setForm({ version: "", notes: "" });
      setFile(null);
      fetch_();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengunggah");
    } finally {
      setUploading(false);
    }
  };

  const activate = async (fw) => {
    if (!window.confirm(`Aktifkan ${fw.version}? Semua ESP32 akan auto-update ke versi ini.`)) return;
    try {
      await api.post(`/projects/${currentId}/firmwares/${fw.id}/activate`);
      toast.success(`${fw.version} aktif`);
      fetch_();
    } catch (e) {
      toast.error("Gagal");
    }
  };

  const remove = async (fw) => {
    if (!window.confirm(`Hapus firmware ${fw.version}?`)) return;
    try {
      await api.delete(`/projects/${currentId}/firmwares/${fw.id}`);
      toast.success("Dihapus");
      fetch_();
    } catch (e) {
      toast.error("Gagal");
    }
  };

  if (!currentId) return <div className="text-muted-foreground">Pilih proyek terlebih dulu.</div>;

  const active = firmwares.find((f) => f.active);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div>
      <PageHeader
        title="OTA Firmware"
        overline={current?.name}
        description="Distribusi firmware ESP32 Over-The-Air. Upload .bin, aktifkan, ESP32 akan auto-update lewat /api/firmware/check."
        actions={
          <Button onClick={() => setOpen(true)} data-testid={OTA.uploadButton}>
            <Upload className="w-4 h-4 mr-1" /> Upload Firmware
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="border border-border rounded-lg bg-card p-5">
          <div className="overline text-muted-foreground">Versi Aktif</div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-display text-3xl font-bold">{active?.version || "—"}</span>
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            {active ? `${formatBytes(active.size)} · MD5 ${active.md5.slice(0, 10)}…` : "Belum ada firmware aktif"}
          </div>
        </div>
        <div className="border border-border rounded-lg bg-card p-5">
          <div className="overline text-muted-foreground">Total Versi</div>
          <div className="mt-2 font-display text-3xl font-bold">{firmwares.length}</div>
          <div className="mt-2 text-xs text-muted-foreground">Tersimpan di server</div>
        </div>
        <div className="border border-border rounded-lg bg-card p-5">
          <div className="overline text-muted-foreground">Check Endpoint</div>
          <div className="mt-2 font-mono text-xs break-all">{backendUrl}/api/projects/.../firmware/check</div>
          <div className="mt-2 text-xs text-muted-foreground">ESP32 GET dengan X-Device-Key</div>
        </div>
      </div>

      <div className="border border-border rounded-lg bg-card mb-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Versi</TableHead>
              <TableHead>Ukuran</TableHead>
              <TableHead>MD5</TableHead>
              <TableHead>Diunggah</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {firmwares.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  Belum ada firmware. Klik "Upload Firmware" untuk memulai.
                </TableCell>
              </TableRow>
            )}
            {firmwares.map((fw) => (
              <TableRow key={fw.id} data-testid={`firmware-row-${fw.id}`}>
                <TableCell className="font-mono font-medium flex items-center gap-2">
                  <Package className="w-4 h-4 text-muted-foreground" />
                  {fw.version}
                </TableCell>
                <TableCell className="font-mono text-xs">{formatBytes(fw.size)}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{fw.md5.slice(0, 12)}…</TableCell>
                <TableCell className="text-xs text-muted-foreground">{new Date(fw.uploaded_at).toLocaleString()}</TableCell>
                <TableCell>
                  {fw.active ? (
                    <Badge className="bg-primary/10 text-primary border-primary/20">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> AKTIF
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">Idle</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {!fw.active && (
                    <Button variant="outline" size="sm" onClick={() => activate(fw)} data-testid={`firmware-activate-${fw.id}`}>
                      <Wifi className="w-4 h-4 mr-1" /> Aktifkan
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => remove(fw)} data-testid={`firmware-delete-${fw.id}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Alert>
        <Activity className="w-4 h-4" />
        <AlertDescription className="text-sm">
          <strong>Cara ESP32 OTA self-update</strong>: tambahkan loop polling tiap 1 jam ke{" "}
          <code className="font-mono">GET /api/projects/{currentId}/firmware/check?current=&lt;current_version&gt;</code>
          dengan header <code className="font-mono">X-Device-Key</code>. Jika respons{" "}
          <code className="font-mono">update_available: true</code>, panggil{" "}
          <code className="font-mono">httpUpdate.update(download_url)</code> (library{" "}
          <code className="font-mono">HTTPUpdate</code>) untuk flash on-the-fly. Snippet ada di Setup Wizard tab Sketch.
        </AlertDescription>
      </Alert>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Firmware</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-2">
              <Label>Version (mis. v1.2.3)</Label>
              <Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} required placeholder="v1.0.0" data-testid={OTA.uploadVersion} />
            </div>
            <div className="space-y-2">
              <Label>File .bin (max 8 MB)</Label>
              <Input type="file" accept=".bin" onChange={(e) => setFile(e.target.files[0])} required data-testid={OTA.uploadFile} />
              {file && <p className="text-xs text-muted-foreground">{file.name} — {formatBytes(file.size)}</p>}
            </div>
            <div className="space-y-2">
              <Label>Catatan (opsional)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Bug fix sensor flowmeter…" data-testid={OTA.uploadNotes} />
            </div>
            {file && file.size > 8 * 1024 * 1024 && (
              <Alert variant="destructive">
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription>File terlalu besar (&gt;8 MB)</AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button type="submit" disabled={uploading} data-testid={OTA.uploadSubmit}>
                {uploading ? "Mengunggah…" : "Upload"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
